import ViewSkeleton from "Components/Common/ViewSkeleton";
import React, { useEffect, useRef, useState } from "react";
import TruncatedText from "Components/Common/TruncatedText";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import SignatureCanvas from "react-signature-canvas";

import { VolunteerFormValues } from "modules/Volunteer/MasterVolunteer/helper/volunteer.types";
import CommonForm from "Components/Common/CommonForm";
import { useForm } from "react-hook-form";
import addVolunteerStyle from "../css/volunteer.module.scss";
import SimpleBar from "simplebar-react";
import { useVolunteerService } from "modules/Volunteer/MasterVolunteer/api/useVolunteerQuery";
import { queryClient } from "api/queryClient";
import { GetUserData, showToastMessage } from "common/service/CommonService";
import FilePreviewModal from "Components/Common/FilePreviewModal";
import profileImage from "../../../../assets/images/volunteer.png";
import { loadCodelistsDetailId } from "api/modules/common.option";
import FaceScanner from "modules/Volunteer/MasterVolunteer/FaceScan/FaceScanner";
import CropPhotoModal from "modules/Volunteer/MasterVolunteer/FaceScan/CropPhotoModal";
import Reason, { ReasonSubmitPayload } from "common/utilities/Reason/Reason";
import AttachmentUploadModal from "./AttachmentUploadModal";
import { Attachment_OPTIONS } from "api/modules/common.option";
import { getFileIcon, VolunteerFields, VolunteerSections } from "../helper/volunteer.constant";
import { normalizeDOB } from "helpers/constant";

interface Props {
  setIsAcknowledged?: React.Dispatch<React.SetStateAction<boolean>>;
  mode?: string | null;
  toggle: () => void;
  isOpen: boolean;
  selectedVolunteerId: number | null;
  fetchedVolunteerData?: Partial<VolunteerFormValues> | null;
  setVolunteerId: React.Dispatch<React.SetStateAction<number | null>>;
}

const AddVolunteer: React.FC<Props> = ({
  isOpen,
  mode,
  toggle,
  fetchedVolunteerData,
  selectedVolunteerId,
  setVolunteerId,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const sigPadRef = useRef<SignatureCanvas | null>(null);

  const [signature, setSignature] = useState<string | null>(null);
  const [signatureError, setSignatureError] = useState<string | null>(null);
  const [signatureModal, setSignatureModal] = useState(false);

  const [attachments, setAttachments] = useState<any[]>([]);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewFileUrl, setPreviewFileUrl] = useState<string | null>(null);
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState<number[]>(
    [],
  );
  const [previewOpen, setPreviewOpen] = useState(false);
  const [retinaImage, setRetinaImage] = useState<string | null>(
    "https://dummyimage.com/300x120/000/fff&text=Retina+Scan+Preview",
  );
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);
  const [isFaceScannerOpen, setIsFaceScannerOpen] = useState<boolean>(false);
  const [pendingCapture, setPendingCapture] = useState<string>("");
  const [isCropOpen, setIsCropOpen] = useState<boolean>(false);
  const [profilePhotoError, setProfilePhotoError] = useState<string | null>(
    null,
  );

  const [attachmentModalOpen, setAttachmentModalOpen] = useState(false);

  const [isReasonModal, setIsReasonModal] = useState<boolean>(false);
  const [pendingUpdatePayload, setPendingUpdatePayload] = useState<any | null>(
    null,
  );

  const { InsertMutation, UpdateMutation } = useVolunteerService({
    otherid: null,
    pageNumber: 1,
    pageSize: 10,
  });

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<VolunteerFormValues>({
    defaultValues: {
      fullname: "",
      gender: null,
      dob: "",
      age: null,
      mobilenum: "",
      email: "",
      address: "",
      city: "",
      state: "",
      pincode: "",
      emergencyname: "",
      emergencynumber: "",
      height: "",
      weight: "",
      bmi: null,
      smoking: null,
      alcohol: null,
      bloodgroup: null,
      allergies: "",
      medicalhistory: "",
      currentmedication: "",
      maritalstatus: "",
      ethnicity: "",
      religion: "",
      occupation: "",
      educationlevel: "",
      primarylanguage: "",
      foodhabit: "",
      populationgroup: "",
      literatestatus: "",
      race: "",
      bac: "",
      indicatorlightcolor: "",
      alcoholunit: "mg/dL",
      alcoholresult: "",
      alcoholtestid: "",
      pulserate: "",
      temperature: "",
    },
    shouldFocusError: true,
  });

  useEffect(() => {
    const isEditMode = mode === "edit" && selectedVolunteerId;
    if (
      fetchedVolunteerData &&
      Object.keys(fetchedVolunteerData).length > 0 &&
      isEditMode
    ) {
      reset({
        ...fetchedVolunteerData,
        dob: normalizeDOB(fetchedVolunteerData.dob),
      });
      // side states
      const rawProfile =
        (fetchedVolunteerData as any)?.profileimg ??
        (fetchedVolunteerData as any)?.profileImage ??
        (fetchedVolunteerData as any)?.image ??
        null;

      setProfilePhoto(rawProfile ? String(rawProfile) : null);

      const rawSignature = (fetchedVolunteerData as any)?.signature ?? null;
      setSignature(rawSignature ? String(rawSignature) : null);
      // ================= ALCOHOL TEST MAPPING =================
      const alcohol = (fetchedVolunteerData as any)?.alcoholtest?.[0];

      if (alcohol) {
        setValue("bac", alcohol.bac ?? "");
        setValue("indicatorlightcolor", alcohol.indicatorlightcolor ?? (fetchedVolunteerData as any).indicatorlightcolor ?? "");
        setValue("alcoholunit", alcohol.alcoholunit ?? "mg/dL");
        setValue("alcoholresult", alcohol.alcoholresult ?? "");
        setValue("alcoholtestid", alcohol.alcoholtestid ?? "");
      }
      setAttachments(fetchedVolunteerData.attachments ?? []);
    } else {
      reset({ fullname: "", age: null, bmi: null, alcoholunit: "mg/dL" }); // reset to defaultValues
      setSignature(null);
      setProfilePhoto(null);
      setAttachments([]);
    }
  }, [fetchedVolunteerData, reset, selectedVolunteerId, isOpen, mode]);

  // ----------------cancel -----------------//
  const clearAllState = () => {
    // Reset form fields
    reset({ fullname: "", age: null, bmi: null, alcoholunit: "mg/dL", pulserate: "", temperature: "", indicatorlightcolor: "" });
    // Clear images
    setProfilePhoto(null);
    setRetinaImage(
      "https://dummyimage.com/300x120/000/fff&text=Retina+Scan+Preview",
    );

    // Clear signature
    setSignature(null);
    setSignatureError(null);
    sigPadRef.current?.clear();

    // Clear attachments
    setAttachments([]);

    // Close modals
    setSignatureModal(false);
    setPreviewOpen(false);
    setIsFaceScannerOpen(false);
    setIsCropOpen(false);
    setIsReasonModal(false);
    setPendingUpdatePayload(null);
    setProfilePhotoError(null);

    // Clear temp capture
    setPendingCapture("");
  };
  const handleCloseModal = () => {
    setVolunteerId(null);
    clearAllState();
    toggle();
  };

  const toggleReasonModal = (): void => {
    setIsReasonModal((prev) => !prev);
  };
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isOpen) return;
    setProfilePhotoError(null);
    setSignatureError(null);
    setIsFaceScannerOpen(false);
    setIsCropOpen(false);
    setPendingCapture("");
  }, [isOpen]);

  /* ================= ProfileImage ================= */

  // utils
  const joinUrl = (base: string | undefined, path: string) =>{
     if(base) {
      return `${base.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
  }
  }
  // render
    const API_FILE_BASE_URL =  sessionStorage.getItem("filebaseUrl") || process.env.REACT_APP_FILE_BASE_URL
    const baseUrl = API_FILE_BASE_URL;
  const serverPath = fetchedVolunteerData?.profileimg ?? null;

  const imageSrc = profilePhoto
    ? profilePhoto.startsWith("data:image")
      ? profilePhoto
      : joinUrl(baseUrl, profilePhoto)
    : serverPath
      ? joinUrl(baseUrl, serverPath)
      : profileImage;
  /* ================= SIGNATURE ================= */

  const serverSignaturePath = fetchedVolunteerData?.signature ?? null;

  const signatureUrl = signature
    ? signature.startsWith("data:image")
      ? signature
      : joinUrl(baseUrl, signature)
    : serverSignaturePath
      ? joinUrl(baseUrl, serverSignaturePath)
      : null;
  const openSignatureModal = () => setSignatureModal(true);

  const closeSignatureModal = () => {
    setSignatureModal(false);
    setSignatureError(null);
  };

  const clearSignature = () => sigPadRef.current?.clear();

  const confirmSignature = () => {
    if (!sigPadRef.current || sigPadRef.current.isEmpty()) {
      // setSignatureError("Signature is required");
      return;
    }

    const base64 = sigPadRef.current.toDataURL();
    setSignature(base64);
    closeSignatureModal();
  };

  const onInvalid = (formErrors: any) => {
    const firstErrorField = Object.keys(formErrors)[0];
    if (!firstErrorField) return;

    const errorElement = document.querySelector(
      `[name="${firstErrorField}"]`,
    ) as HTMLElement | null;

    if (errorElement) {
      errorElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      errorElement.focus();
    }
  };

  const handleDelete = (id: number) => {
    setAttachments((prev) =>
      prev.filter((item) => (item.id ?? item.attachmentid) !== id),
    );

    const deletedItem = attachments.find(
      (item) => (item.id ?? item.attachmentid) === id,
    );

    if (deletedItem?.attachmentid) {
      setDeletedAttachmentIds((prev) => [...prev, deletedItem.attachmentid]);
    }
  };

  /* ================= ATTACHMENTS ================= */

  const handleAttachmentSave = (fileData: any[]) => {
    setAttachments((prev) => [...prev, ...fileData]);
  };



  const handlePreview = (file: File, url: string | null) => {
    const API_FILE_BASE_URL =  sessionStorage.getItem("filebaseUrl") || process.env.REACT_APP_FILE_BASE_URL
    const base = API_FILE_BASE_URL;
    if (file) {
      setPreviewFile(file);
      setPreviewFileUrl(null);
    }
    if (url) {
      setPreviewFileUrl(`${base}${url}`);
      setPreviewFile(null);
    }
    setPreviewOpen(true);
  };


  /* ================= SUBMIT ================= */

  const onSubmit = (values: VolunteerFormValues) => {
    let hasError = false;

    const hasRCF = attachments.some((att) => {
      const val = String(
        att.attachmentType || att.attachmenttype || "",
      ).toLowerCase();
      const label = String(att.typeLabel || "")
        .toLowerCase()
        .replace(/\s+/g, "");
      const option = Attachment_OPTIONS.find(
        (opt) => String(opt.value) === val,
      );
      const optLabel = option
        ? String(option.label).toLowerCase().replace(/\s+/g, "")
        : "";
      return val === "rcf" || label === "rcf" || optLabel === "rcf";
    });

    const hasIDPROOF = attachments.some((att) => {
      const val = String(
        att.attachmentType || att.attachmenttype || "",
      ).toLowerCase();
      const label = String(att.typeLabel || "")
        .toLowerCase()
        .replace(/\s+/g, "");
      const option = Attachment_OPTIONS.find(
        (opt) => String(opt.value) === val,
      );
      const optLabel = option
        ? String(option.label).toLowerCase().replace(/\s+/g, "")
        : "";
      return (
        val.includes("idproof") ||
        label.includes("idproof") ||
        optLabel.includes("idproof") ||
        val === "id proof" ||
        optLabel === "id proof"
      );
    });

    if (!hasRCF || !hasIDPROOF) {
      showToastMessage(
        "error",
        "Both RCF and ID Proof attachments are mandatory.",
      );
      hasError = true;
    }

    if (hasError) return;
    setSignatureError(null);

    const formattedAttachments = attachments.map((item) => ({
      filename: item.filename,
      fileextension: item.fileextension,
      size: item.size,
      file: item.file,
      type: item.attachmentType,
    }));

    const payload = {
      ...values,
      dob: normalizeDOB(values.dob),
      attachments: formattedAttachments,
      signature,
      alcoholtesttype: "ALCOHOL",
      profileimg: profilePhoto,
    };
    if (mode === "add") {
      InsertMutation.mutate(payload, {
        onSuccess: (res) => {
          showToastMessage("success", res?.message || "Inserted successfully");
          queryClient.invalidateQueries({ queryKey: ["volunteer-selectAll"] });
          toggle();
        },
        onError: (err: any) => {
          showToastMessage(
            "error",
            err || "Something went wrong",
          );
        },
      });
    } else {
      if (!selectedVolunteerId) {
        showToastMessage("error", "Volunteer ID missing");
        return;
      }
      const updatePayload = {
        ...values,
        dob: normalizeDOB(values.dob),
        attachments: formattedAttachments,
        signature,
        alcoholtesttype: "ALCOHOL",
        profileimg: profilePhoto,
        volunteerid: selectedVolunteerId,
        deletedAttachmentIds: deletedAttachmentIds.join(","),
      };

      // Open reason modal before updating
      setPendingUpdatePayload(updatePayload);
      setIsReasonModal(true);
    }
  };

  const btnReasonClick = (reasonPayload: ReasonSubmitPayload) => {
    if (!pendingUpdatePayload || !selectedVolunteerId) return;

    UpdateMutation.mutate(
      {
        ...pendingUpdatePayload,
        updatereason: reasonPayload.reason,
      },
      {
        onSuccess: (res) => {
          showToastMessage(
            "success",
            res?.message || "Volunteer updated successfully",
          );

          // refresh list
          queryClient.invalidateQueries({ queryKey: ["volunteer-selectAll"] });

          queryClient.invalidateQueries({
            queryKey: ["volunteer-select", selectedVolunteerId],
          });
          setDeletedAttachmentIds([]);
          // clear form + close modal
          clearAllState();
          toggle();
        },

        onError: (err: any) => {
          console.error("Update Error:", err);
          showToastMessage(
            "error",
            err || "Update failed",
          );
        },
      },
    );
  };

  /* ================= UI ================= */
  const handleRetinaCapture = () => {
    const dummy =
      "https://dummyimage.com/300x120/000/fff&text=Retina+Scan+Captured";

    setRetinaImage(dummy);
  };

  useEffect(() => {
    const load = async () => {
      const result = await loadCodelistsDetailId([
        "Gender_OPTIONS",
        "BloodGroup_OPTIONS",
        "MedicalCode_OPTIONS",
        "IDPROOF_OPTIONS",
        "MaritalStatus_OPTIONS",
        "Ethnicity_OPTIONS",
        "Religion_OPTIONS",
        "Occupation_OPTIONS",
        "EducationLevel_OPTIONS",
        "PrimaryLanguage_OPTIONS",
        "FoodHabit_OPTIONS",
        "PopulationGroup_OPTIONS",
        "LiterateStatus_OPTIONS",
        "Race_OPTIONS",
        "AlcoholResult_OPTIONS",
        "Attachment_OPTIONS",
        "SMOKING_OPTIONS",
      ]);
      // setAttachmentOptions(result.Attachment_OPTIONS || []);

      const allLoaded = Object.values(result).every((flag) => flag);
      if (!allLoaded) {
        showToastMessage("error", "Some dropdowns failed to load");
      }
    };
    load();
  }, []);

  /* ================= AUTO CALCULATE AGE ================= */

  const dob = watch("dob");
  const age = watch("age");

  useEffect(() => {
    if (!dob) {
      setValue("age", null);
      return;
    }

    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    if (age < 0) {
      age = 0;
    }

    if (!isNaN(age)) {
      setValue("age", age);
    }
  }, [dob, setValue]);

  /* ================= AUTO CALCULATE BMI ================= */

  const height = watch("height");
  const weight = watch("weight");

  useEffect(() => {
    const h = Number(height);
    const w = Number(weight);

    if (!h || !w || h <= 0) {
      setValue("bmi", null);
      return;
    }

    const heightInMeter = h / 100;
    const bmi = w / (heightInMeter * heightInMeter);

    if (!isNaN(bmi) && isFinite(bmi)) {
      setValue("bmi", Number(bmi.toFixed(2)));
    } else {
      setValue("bmi", null);
    }
  }, [height, weight, setValue]);

  /* ================= FILTERED ATTACHMENT OPTIONS ================= */

  const filteredAttachmentOptions = React.useMemo(() => {
    // Get all existing types from local state (added) and server state (fetched)
    const existingTypes = attachments.map((att) =>
      String(att.attachmentType || att.attachmenttype || "").toLowerCase(),
    );

    return Attachment_OPTIONS.filter((opt) => {
      const value = String(opt.value).toLowerCase();
      const label = String(opt.label).toLowerCase();
      // Hide if either the value or label matches existing attachments
      return !existingTypes.includes(value) && !existingTypes.includes(label);
    });
  }, [attachments]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={handleCloseModal}
        size="xl"
        fullscreen
        backdrop="static"
      >
        <ModalHeader
          toggle={handleCloseModal}
          className="fw-bold border-bottom p-3 border-bottom"
        >
          {mode === "add" ? "Add" : "Edit"} Volunteer Details
        </ModalHeader>

        <ModalBody>
          {loading ? (
            <ViewSkeleton sections={4} fieldsPerSection={4} />
          ) : (
            <Container fluid className="px-3">
              <Row
                className={`mb-3 ${addVolunteerStyle.add_volunteer_wrapper} gap-2`}
              >
                {/* LEFT SECTION */}
                <Col
                  lg={2}
                  className={`p-0 m-0 ${addVolunteerStyle.left_section}`}
                >
                  <Card className={`${addVolunteerStyle.card_wrapper} mb-0`}>
                    <CardBody className="text-center">
                      <div className={addVolunteerStyle.profile_wrapper}>
                        <button
                          type="button"
                          className={`${addVolunteerStyle.avatar_wrapper} p-0 bg-transparent`}
                          style={{
                            border:
                              !profilePhoto && profilePhotoError
                                ? "3px solid red"
                                : "none",
                            borderRadius: "50%",
                          }}
                          onClick={() => setIsFaceScannerOpen(true)}
                          aria-label="Capture volunteer photo"
                        >
                          <img
                            src={imageSrc}
                            alt="Volunteer"
                            className={addVolunteerStyle.avatar_image}
                          />
                        </button>
                      </div>
                      {!profilePhoto && profilePhotoError && (
                        <div className="text-danger small mt-1 text-start">
                          {profilePhotoError}
                        </div>
                      )}

                      {/* Signature */}
                      <div className={addVolunteerStyle.signature_wrapper}>
                        <h6 className="text-start">Signature</h6>

                        <div
                          className={`${addVolunteerStyle.signature_box} ${!signature && signatureError ? "border border-danger" : ""}`}
                          onClick={openSignatureModal}
                        >
                          {signatureUrl ? (
                            <img
                              src={signatureUrl || undefined}
                              className={addVolunteerStyle.signature_image}
                              alt="Signature"
                            />
                          ) : (
                            <div
                              className={
                                addVolunteerStyle.signature_placeholder
                              }
                            >
                              Click to add signature
                            </div>
                          )}
                        </div>
                        {!signature && signatureError && (
                          <div className="text-danger small mt-1">
                            {signatureError}
                          </div>
                        )}
                      </div>

                      <div className={addVolunteerStyle.retina_wrapper}>
                        <div className={addVolunteerStyle.retina_header}>
                          <h6 className="mb-0">Retina Scan</h6>{" "}
                          {retinaImage && (
                            <span
                              className={addVolunteerStyle.retina_edit}
                              onClick={handleRetinaCapture}
                            >
                              Re-scan{" "}
                            </span>
                          )}{" "}
                        </div>
                        <div
                          className={addVolunteerStyle.retina_box}
                          onClick={handleRetinaCapture}
                        >
                          {" "}
                          <div className={addVolunteerStyle.retina_placeholder}>
                            {" "}
                            <i className="ri-eye-line" />{" "}
                            <span>Click to capture retina</span>{" "}
                          </div>{" "}
                        </div>{" "}
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                {/* RIGHT SECTION */}
                <Col
                  lg={10}
                  className={`${addVolunteerStyle.right_section} p-0`}
                >
                  <Card className={addVolunteerStyle.card_wrapper}>
                    <CardBody
                      className="p-0"
                      style={{
                        height: "calc(100vh - 154px)",
                        overflow: "hidden",
                      }}
                    >
                      <SimpleBar style={{ height: "100%" }} className="p-3">
                        {VolunteerSections.map((section, index) => (
                          <div
                            key={index}
                            className={addVolunteerStyle.section_wrapper}
                          >
                            <div className={addVolunteerStyle.section_header}>
                              {section.title}
                            </div>

                            <CommonForm
                              fields={VolunteerFields.filter((field) =>
                                section.fields.includes(
                                  field.name as keyof VolunteerFormValues,
                                ),
                              )}
                              control={control}
                              errors={errors}
                              columns={3}
                              disableFuture
                              setValue={setValue}
                            />
                          </div>
                        ))}

                        {/* Attachments */}

                        <div className={addVolunteerStyle.attachmentSection}>
                          <div className={addVolunteerStyle.attachmentHeader}>
                            <h6>
                              Attachments <span className="text-danger">*</span>
                            </h6>
                            {filteredAttachmentOptions.length > 0 && (
                              <Button
                                size="sm"
                                color="primary"
                                outline
                                onClick={() => setAttachmentModalOpen(true)}
                              // onClick={handleAddAttachment}
                              >
                                <i className="ri-upload-2-line me-1" />
                                Add Attachment
                              </Button>
                            )}
                          </div>


                          <Row className="mt-3 gap-2 w-100 mx-auto">
                            {attachments.length === 0 ? (
                              <div className="text-muted small mt-2">
                                No attachments available.
                              </div>
                            ) : (
                              attachments.map((item) => (
                                <Col
                                  key={item.id ?? item.attachmentid}
                                  md={4}
                                  sm={12}
                                  className={`p-0 ${addVolunteerStyle.attachmentCard_wrapper}`}
                                >
                                  <div
                                    className={addVolunteerStyle.attachmentCard}
                                  >
                                    <div className={addVolunteerStyle.fileInfo}>
                                      <div
                                        className={addVolunteerStyle.fileIcon}
                                      >
                                        <i
                                          className={getFileIcon(
                                            item.fileextension ??
                                            item.extension,
                                          )}
                                        />
                                      </div>

                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div
                                          className={addVolunteerStyle.fileName}
                                        >
                                          <TruncatedText text={item.filename ?? item.name} />
                                        </div>
                                        <div
                                          className={addVolunteerStyle.fileMeta}
                                        >
                                          {item.size ??
                                            (item.file
                                              ? `${(item.file.size / 1024).toFixed(2)} KB`
                                              : "")}
                                        </div>
                                        <div className="small text-muted">
                                          Type :{" "}
                                          {String(
                                            item.typeLabel || item.attachmenttypetext || "",
                                          ).toUpperCase()}
                                        </div>
                                      </div>
                                    </div>

                                    <div
                                      className={addVolunteerStyle.fileActions}
                                    >
                                      <i
                                        className="ri-eye-line"
                                        title="Preview"
                                        onClick={() =>
                                          handlePreview(item.file, item.path)
                                        }
                                      />

                                      <i
                                        className="ri-delete-bin-line text-danger"
                                        title="Delete"
                                        // onClick={() => handleDelete(item.attachmentid)}
                                        onClick={() =>
                                          handleDelete(
                                            item.id ?? item.attachmentid,
                                          )
                                        }
                                      />
                                    </div>
                                  </div>
                                </Col>
                              ))
                            )}
                          </Row>
                        </div>
                      </SimpleBar>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </Container>
          )}
        </ModalBody>

        <ModalFooter className={` ${addVolunteerStyle.button_container}`}>
          <Button
            outline
            onClick={handleCloseModal}

          >

            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSubmit(onSubmit, onInvalid)}
            disabled={InsertMutation.isPending}
          >
            {InsertMutation.isPending && <Spinner size="sm" className="me-2" />}
            Save Volunteer
          </Button>
        </ModalFooter>
      </Modal>

      {/* Signature Modal */}
      <Modal isOpen={signatureModal} toggle={closeSignatureModal} size="lg">
        <ModalHeader toggle={closeSignatureModal}>Draw Signature</ModalHeader>
        <ModalBody>
          <div style={{ border: "1px solid #ccc", height: 200 }}>
            <SignatureCanvas
              ref={sigPadRef}
              canvasProps={{ width: 700, height: 200 }}
            />
          </div>
          {signatureError && <Alert color="danger">{signatureError}</Alert>}
        </ModalBody>
        <ModalFooter>
          <Button size="sm" onClick={clearSignature}>
            Clear
          </Button>
          <Button color="primary" onClick={confirmSignature}>
            Confirm
          </Button>
        </ModalFooter>
      </Modal>

      <FilePreviewModal
        file={previewFile}
        fileUrl={previewFileUrl}
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
      />

      <FaceScanner
        isOpen={isFaceScannerOpen}
        onClose={() => setIsFaceScannerOpen(false)}
        onCaptured={(imageDataUrl) => {
          setPendingCapture(imageDataUrl);
          setIsCropOpen(true);
        }}
      />

      <CropPhotoModal
        isOpen={isCropOpen}
        imageSrc={pendingCapture}
        onClose={() => setIsCropOpen(false)}
        onSave={(croppedDataUrl) => {
          setProfilePhoto(croppedDataUrl);
          setProfilePhotoError(null);
          setPendingCapture("");
        }}
      />

      <AttachmentUploadModal
        isOpen={attachmentModalOpen}
        toggle={() => setAttachmentModalOpen(false)}
        onSave={handleAttachmentSave}
        options={filteredAttachmentOptions}
        existingAttachments={attachments}
      />

      {isReasonModal && (
        <Reason
          isOpen={isReasonModal}
          toggle={toggleReasonModal}
          action="Update"
          id={selectedVolunteerId}
          click={btnReasonClick}
          isLoading={UpdateMutation.isPending}
        />
      )}
    </>
  );
};

export default AddVolunteer;
